<template>
  <div class="messages" :style="{ maxHeight: maxHeight }">
    <DynamicScroller
      keyField="_id"
      :items="messagesWithDaySeparator"
      :min-item-size="64"
      :buffer="500"
      class="scroller"
      ref="scroller"
    >
      <template v-slot="{ item, index, active }">
        <DynamicScrollerItem
          :item="item"
          :active="active"
          :size-dependencies="[
            item.text,
            item.document,
            item.photo,
            item.voice,
            item.videoNote,
            item.video,
          ]"
          :data-index="index"
        >
          <div
            :key="item._id"
            style="padding: 0.25rem 1rem; display: flex; position: relative"
            :class="{ highlight: index === highlightIndex }"
          >
            <div
              v-if="item.type === 'separator'"
              class="flex-grow-1 text-center py-6"
            >
              <v-chip
                :color="$vuetify.theme.dark ? 'primary' : 'green'"
                :outlined="$vuetify.theme.dark ? true : false"
                dark
              >
                {{ item.text }}
              </v-chip>
            </div>

            <message
              v-else
              :message="item"
              @view-img="viewImage"
              @open-menu="openMenu"
            ></message>
          </div>
        </DynamicScrollerItem>
      </template>
    </DynamicScroller>

    <v-fab-transition>
      <v-btn
        @click="scrollToBottom"
        v-if="scrollButton"
        class="scroll-btn"
        color="blue"
        dark
        absolute
        fab
      >
        <v-badge
          color="pink"
          :content="currentChat?.unreadMessagesCount"
          :value="currentChat?.unreadMessagesCount"
        >
          <v-icon>mdi-arrow-down</v-icon>
        </v-badge>
      </v-btn>
    </v-fab-transition>

    <message-menu
      :show="showMenu"
      :x="x"
      :y="y"
      :has-selected-messages="hasSelectedMessages"
      :is-selected="isSelected"
      :is-history-message="isHistoryMessage()"
      :menu-message="menuMessage"
      :loading="loading"
      :currentSupportAzureId="currentSupport?.azureProviderId"
      :gptResponseLoading="gptResponseLoading"
      @forwardMessage="forwardMessage"
      @deleteMessages="deleteMessages"
      @clearSelected="clearSelected"
      @selectMessage="selectMessage"
      @selectColor="color => selectColor(color)"
      @commentMessage="commentMessage"
      @replyToMessage="replyToMessage"
      @editMessage="editMessage"
      @deleteMessage="deleteMessage"
      @rateMessage="rateMessage"
      @getGptResponse="getGptResponse"
      @copyMessageData="copyMessageData"
    />

    <forward-message-dialog-v2 />

    <comment-dialog />
    <upload-dialog></upload-dialog>
    <upload-edit-dialog></upload-edit-dialog>
    <loader-dialog></loader-dialog>

    <generic-loader-dialog
      :cancel-button-text="'Відмінити'"
      :message="' Пошук повідомлення. Зачекайте, будь ласка.'"
      :show="this.scrollingToMessage"
      @hide="() => this.setScrollingToMessage(false)"
    />

    <v-overlay
      class="loading-messages-overlay"
      absolute
      :value="this.fetchingMessages"
    >
      <v-progress-circular indeterminate size="64" />
    </v-overlay>
  </div>
</template>

<script>
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import ForwardMessageDialogV2 from '@/components/dialogs/ForwardMessageDialogV2';
import CommentDialog from '@/components/dialogs/CommentDialog';
import UploadDialog from '@/components/dialogs/UploadDialog';
import UploadEditDialog from '@/components/dialogs/UploadEditDialog';
import LoaderDialog from '@/components/dialogs/LoaderDialog';
import GenericLoaderDialog from './dialogs/GenericLoaderDialog.vue';
import Message from '@/components/Message';
import MessageMenu from '@/components/MessageMenu';

import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import {
  getObjectIndexByKeyValue,
  sameDay,
  formatMessageSeparatorDate,
} from '@/lib/utils';
import { api as viewerApi } from 'v-viewer';
import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'Messages',
  components: {
    Message,
    MessageMenu,
    CommentDialog,
    UploadDialog,
    UploadEditDialog,
    LoaderDialog,
    GenericLoaderDialog,
    DynamicScroller,
    DynamicScrollerItem,
    ForwardMessageDialogV2,
  },

  props: {
    maxHeight: String,
    // ... other props
  },

  data() {
    return {
      highlightIndex: null,
      highlightTimeout: null,

      finished: false,
      prevMessagesCount: 0,
      loading: false,
      delete: false,
      menuMessage: null,
      menuMessageTimeout: null,
      showMenu: false,
      scrollButton: false,
      x: 0,
      y: 0,

      topSentinel: null,
      intersectionObserver: null,
      onScrollCallback: null,
    };
  },

  computed: {
    byId() {
      return this.$store.state.chat.byId;
    },

    currentChat() {
      return this.$store.getters['chat/currentChat'];
    },

    messages() {
      return this.$store.getters['chat/messages'];
    },

    scrollToMessageId() {
      return this.$store.state.chat.scrollToMessageId;
    },

    searchDate() {
      return this.$store.state.chat.searchDate;
    },

    scrollToBottomStateValue() {
      return this.$store.state.ui.scrollToBottom;
    },

    messagesWithDaySeparator() {
      const tempMessages = this.$store.getters['chat/messages'];
      const messagesWithDaySeparator = [];

      if (tempMessages.length) {
        tempMessages.forEach((message, index) => {
          messagesWithDaySeparator.push(message);

          if (
            tempMessages[index + 1] &&
            !sameDay(message.createdAt, tempMessages[index + 1].createdAt)
          ) {
            messagesWithDaySeparator.push({
              _id: uuidv4(),
              type: 'separator',
              text: formatMessageSeparatorDate(
                tempMessages[index + 1].createdAt
              ),
            });
          }
        });
      }
      return messagesWithDaySeparator;
    },

    photoMessageSources() {
      let blobContainerUrl = `${process.env.VUE_APP_BLOB_URL}${process.env.VUE_APP_BLOB_CONTAINER_NAME}/`;

      return this.messages
        .filter(m => m.hasOwnProperty('photo'))
        .map(m => {
          return {
            src: blobContainerUrl + m.photo.fileName,
          };
        });
    },

    selectedMessages() {
      return this.$store.state.chat.selectedMessages;
    },

    hasSelectedMessages() {
      return this.$store.getters['chat/hasSelectedMessages'];
    },

    isSelected() {
      if (!this.menuMessage) return false;
      return (
        this.$store.getters['chat/selectedMessagesIds'].indexOf(
          this.menuMessage._id
        ) >= 0
      );
    },

    lastAction() {
      return this.$store.getters['ui/lastAction'];
    },

    currentSupport() {
      return this.$store.getters['support/currentSupport'];
    },

    getMoreMessagesAction() {
      return 'chat/get_more_chat_messages_v2';
    },

    fetchingMessages() {
      return this.$store.state.chat.fetchingMessages;
    },

    scrollingToMessage() {
      return this.$store.state.ui.scrollingToMessage;
    },

    gptResponseLoading() {
      return this.$store.state.gpt.loading;
    },
  },

  watch: {
    showMenu(val) {
      val
        ? this.$store.commit('ui/ADD_ACTION', 'open-context-menu', {
            root: true,
          })
        : this.$store.commit('ui/REMOVE_ACTION_IF_LAST', 'open-context-menu', {
            root: true,
          });
    },

    currentChat(newChat, oldChat) {
      this.finished = false;

      if (this.scrollingToMessage) return;

      if (newChat.chatId !== oldChat.chatId) {
        if (this.messages.length <= 50) {
          this.$store
            .dispatch(
              this.getMoreMessagesAction,
              { chatId: this.currentChat.chatId },
              { root: true }
            )
            .then(() => this.scrollToBottom());
        } else {
          this.scrollToBottom();
        }
      }
    },

    scrollToMessageId(messageId) {
      if (messageId < 0) return;
      this.scrollToMessage(messageId);
    },

    searchDate(date) {
      this.scrollToDate(date);
    },

    scrollToBottomStateValue(value) {
      if (value) {
        this.scrollToBottom();
        this.$store.commit('ui/SET_SCROLL_TO_BOTTOM', false, { root: true });
      }
    },

    messages() {
      const { clientHeight, scrollHeight, scrollTop } = this.$refs.scroller.$el;
      const isOnBottom = scrollTop + clientHeight >= scrollHeight;

      this.$nextTick(() => {
        if (isOnBottom) {
          this.scrollToBottom();
        }
      });
    },
  },

  created() {
    document.addEventListener('keydown', e => {
      e = e || window.event;
      //escape
      if (e.keyCode === 27) {
        if (this.$store.state.ui.actions.length > 0) {
          if (this.lastAction === 'open-context-menu') {
            if (this.showMenu) this.closeMenu();
          }
        }
      }
    });
  },

  mounted() {
    this.addOnScrollCallback();
    this.setupIntersectionObserver();
  },

  // eslint-disable-next-line vue/no-deprecated-destroyed-lifecycle
  beforeDestroy() {
    this.setScrollingToMessage(false);
    this.removeOnScrollCallback();
    this.cleanupIntersectionObserver();
  },

  methods: {
    sameDay,
    formatMessageSeparatorDate,

    async scrollToMessage(messageId) {
      let messageFetched = this.messagesWithDaySeparator.find(
        message => message.messageId === messageId
      );

      const initialScrollHeight = this.getCurrentScrollHeight();

      try {
        while (!messageFetched && this.scrollingToMessage && !this.finished) {
          const newMessageBatch = await this.$store.dispatch(
            this.getMoreMessagesAction,
            { chatId: this.currentChat.chatId }
          );

          messageFetched = newMessageBatch.find(
            message => message.messageId === messageId
          );

          if (newMessageBatch.length === 0) {
            this.finished = true;
          }
        }
      } catch (e) {
        console.error('Error occured when scrolling to message');
      } finally {
        this.setScrollingToMessage(false);
        this.$store.commit('chat/SET_SCROLL_TO_MESSAGE_ID', { messageId: -1 });
      }

      const messageIndex = this.messagesWithDaySeparator.findIndex(
        message => message.messageId === messageId
      );

      clearTimeout(this.highlightTimeout);
      this.highlightIndex = messageIndex;
      this.highlightTimeout = setTimeout(() => {
        this.highlightIndex = null;
      }, 5000);

      this.restoreScrollPosition(initialScrollHeight);

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.$refs.scroller.scrollToItem(messageIndex);
        });

        setTimeout(() => {
          requestAnimationFrame(() =>
            this.$refs.scroller.scrollToItem(messageIndex)
          );
        });
      });
    },

    async scrollToDate(date) {
      const timestamp = new Date(date).getTime();
      let messageBeforeDateFetched =
        this.messagesWithDaySeparator[0]?.createdAt < timestamp;

      try {
        this.setScrollingToMessage(true);
        while (
          !messageBeforeDateFetched &&
          this.scrollingToMessage &&
          !this.finished
        ) {
          const newMessageBatch = await this.$store.dispatch(
            this.getMoreMessagesAction,
            { chatId: this.currentChat.chatId }
          );

          messageBeforeDateFetched = newMessageBatch[0]?.createdAt < timestamp;

          if (newMessageBatch.length === 0) {
            this.finished = true;
          }
        }
      } catch (e) {
        console.error('Error occured when scrolling to date');
      } finally {
        this.setScrollingToMessage(false);
      }

      const messagesBeforeDate = this.messagesWithDaySeparator.filter(
        message => message?.createdAt < timestamp
      );

      const messageToScrollTo =
        messagesBeforeDate.length === 0
          ? this.messagesWithDaySeparator[0]
          : messagesBeforeDate[messagesBeforeDate.length - 1];

      await this.scrollToMessage(messageToScrollTo.messageId);
    },

    viewImage(src) {
      let getIndex = getObjectIndexByKeyValue(
        this.photoMessageSources,
        src,
        'src'
      );

      const $viewer = viewerApi({
        options: {
          toolbar: true,
          url: 'src',
          initialViewIndex: getIndex || 0,
        },
        images: this.photoMessageSources,
      });

      this.$store.commit('ui/ADD_ACTION', 'open-photo', { root: true });

      $viewer.close = () => {
        this.$store.commit('ui/REMOVE_ACTION_IF_LAST', 'open-photo', {
          root: true,
        });
      };
    },

    commentMessage() {
      if (!this.menuMessage) return;

      this.$store.commit(
        'ui/SET_CURRENT_DIALOG',
        {
          name: 'commentDialog',
          data: {
            message: this.menuMessage,
          },
        },
        { root: true }
      );

      this.closeMenu();
    },

    handleScrollButton() {
      const scrollEl = this.$refs.scroller.$el;
      this.scrollButton =
        scrollEl.scrollHeight - scrollEl.offsetHeight - scrollEl.offsetHeight >=
        scrollEl.scrollTop;
    },

    selectColor(color) {
      if (!this.menuMessage) return;

      this.$store
        .dispatch(
          'chat/set_color',
          {
            chatId: this.menuMessage.chatId,
            messageId: this.menuMessage.messageId,
            color: color,
          },
          { root: true }
        )
        .then(res => {
          this.closeMenu();
        })
        .catch(e => {
          console.error(e);
        });
    },

    clearSelected() {
      if (!this.hasSelectedMessages) return;

      this.$store.commit('chat/SET_SELECTED_MESSAGES', [], { root: true });

      this.closeMenu(false);
    },

    selectMessage() {
      if (!this.menuMessage) return;

      if (this.selectedMessages.length === 0) {
        this.$store.commit('ui/REMOVE_ACTION_IF_LAST', 'open-context-menu', {
          root: true,
        });
        this.$store.commit('ui/ADD_ACTION', 'add-selected-message', {
          root: true,
        });
      }

      this.$store.commit('chat/ADD_SELECTED_MESSAGE', this.menuMessage, {
        root: true,
      });
      this.closeMenu(false);
    },

    forwardMessage() {
      if (!this.menuMessage) return;

      this.$store.commit(
        'ui/SET_CURRENT_DIALOG',
        {
          name: 'forwardMessageDialog',
          data: {
            message: this.menuMessage,
          },
        },
        { root: true }
      );

      this.closeMenu();
    },

    replyToMessage() {
      if (!this.menuMessage) return;

      this.$store.commit('chat/SET_REPLY_TO_MESSAGE', this.menuMessage, {
        root: true,
      });
      document.getElementById('messageInput').focus();

      this.closeMenu();
    },

    editMessage() {
      if (!this.menuMessage) return;

      if (
        this.menuMessage.document ||
        this.menuMessage.photo ||
        this.menuMessage.voice ||
        this.menuMessage.videoNote ||
        this.menuMessage.video
      ) {
        this.$store.commit(
          'ui/SET_CURRENT_DIALOG',
          {
            name: 'uploadEditDialog',
            data: { message: this.menuMessage },
          },
          { root: true }
        );
      } else if (this.menuMessage.text) {
        this.$store.commit('ui/REMOVE_ACTION_IF_LAST', 'open-context-menu', {
          root: true,
        });
        this.$store.commit('ui/ADD_ACTION', 'add-edit-message', { root: true });

        this.$store.commit(
          'chat/SET_CHAT_MODE',
          this.$store.state.chat.chatModes.editMessage,
          { root: true }
        );
        this.$store.commit('chat/SET_CURRENT_MESSAGE', this.menuMessage, {
          root: true,
        });
        this.$store.commit('chat/SET_MESSAGE_INPUT', this.menuMessage.text, {
          root: true,
        });
        document.getElementById('messageInput').focus();
      }

      this.closeMenu();
    },

    async deleteMessage() {
      if (!this.menuMessage) return;

      if (!this.delete) {
        this.delete = true;
        setTimeout(() => {
          this.delete = false;
        }, 500);
        return;
      }

      this.loading = true;

      await this.$store
        .dispatch(
          'chat/delete_message',
          {
            chatId: this.menuMessage.chatId,
            messageId: this.menuMessage.messageId,
          },
          { root: true }
        )
        .then(res => {
          this.closeMenu();
        })
        .catch(e => {
          console.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async deleteMessages() {
      if (!this.delete) {
        this.delete = true;
        setTimeout(() => {
          this.delete = false;
        }, 500);
        return;
      }

      this.closeMenu();

      this.loading = true;
      this.$store.commit(
        'ui/SET_CURRENT_DIALOG',
        { name: 'loaderDialog' },
        { root: true }
      );

      let selectedMessages = [...this.selectedMessages];

      for (let i = 0; i < selectedMessages.length; i++) {
        await this.$store
          .dispatch(
            'chat/delete_message',
            {
              chatId: selectedMessages[i].chatId,
              messageId: selectedMessages[i].messageId,
            },
            { root: true }
          )
          .then(res => {
            this.$store.commit(
              'chat/DELETE_FROM_SELECTED_MESSAGES',
              selectedMessages[i],
              { root: true }
            );

            if (this.selectedMessages.length === 0)
              this.$store.commit(
                'ui/REMOVE_ACTION_IF_LAST',
                'add-selected-message',
                { root: true }
              );
          })
          .catch(e => {
            console.error(e);
          });
      }

      this.loading = false;
      this.$store.commit('ui/SET_CURRENT_DIALOG', {}, { root: true });
    },

    async rateMessage(value) {
      if (!this.menuMessage) return;

      await this.$store
        .dispatch(
          'chat/set_message_rating',
          {
            chatId: this.menuMessage.chatId,
            messageId: this.menuMessage.messageId,
            value,
          },
          { root: true }
        )
        .then(res => {
          this.closeMenu();
        })
        .catch(e => {
          console.error(e);
        });
    },

    async getGptResponse() {
      if (!this.menuMessage) return;

      await this.$store
        .dispatch('gpt/getGptResponse', {
          chatId: this.menuMessage.chatId,
          messageIds: [this.menuMessage.messageId],
        })
        .then(res => {
          this.closeMenu();
        })
        .catch(e => {
          console.error(e);
        });
    },

    async copyMessageData() {
      if (!this.menuMessage) return;

      const messageData = `chatId: ${this.menuMessage.chatId}\nmessageId: ${this.menuMessage.messageId}`;

      try {
        await navigator.clipboard.writeText(messageData);
      } catch (error) {
        console.log(error || 'Did not managed to copy the text');
      } finally {
        this.closeMenu();
      }
    },

    openMenu(e, message) {
      if (this.showMenu) {
        this.closeMenu();
        return;
      }

      clearTimeout(this.menuMessageTimeout);
      this.menuMessageTimeout = null;

      this.x = e.clientX;
      this.y = e.clientY;

      this.$nextTick(() => {
        this.menuMessage = message;
        this.showMenu = true;
      });
    },

    closeMenu(smoothly = true) {
      if (!this.showMenu) return;

      this.showMenu = false;

      if (smoothly) {
        this.menuMessageTimeout = setTimeout(() => {
          this.menuMessage = null;
          this.menuMessageTimeout = null;
        }, 200);
      } else {
        this.menuMessage = null;
        this.menuMessageTimeout = null;
      }
    },

    scrollToBottom() {
      this.$refs.scroller.scrollToBottom();
    },

    addOnScrollCallback() {
      const scroller = this.$refs.scroller.$el;
      this.onScrollCallback = this.handleScrollButton;
      scroller.addEventListener('scroll', this.onScrollCallback);
    },

    removeOnScrollCallback() {
      const scroller = this.$refs.scroller.$el;
      scroller.removeEventListener('scroll', this.onScrollCallback);
    },

    isHistoryMessage() {
      if (!this.menuMessage) return false;

      return this.menuMessage.messageId < 0;
    },

    getCurrentScrollHeight() {
      return this.$refs.scroller.$el.scrollHeight;
    },

    restoreScrollPosition(initialScrollHeight) {
      const scroller = this.$refs.scroller;

      const newScrollHeight = this.getCurrentScrollHeight();
      const difference = newScrollHeight - initialScrollHeight;

      scroller.$el.scrollTo(0, difference);
    },

    async getMoreMessages() {
      const initialScrollHeight = this.getCurrentScrollHeight();
      const firstMessage = this.messagesWithDaySeparator[0];

      const newMessageBatch = await this.$store.dispatch(
        this.getMoreMessagesAction,
        { chatId: this.currentChat.chatId },
        { root: true }
      );

      const firstMessageIndex = this.messagesWithDaySeparator.findIndex(
        ({ messageId }) => messageId === firstMessage.messageId
      );

      this.restoreScrollPosition(initialScrollHeight);

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.$refs.scroller.scrollToItem(firstMessageIndex);
        });

        setTimeout(() => {
          requestAnimationFrame(() =>
            this.$refs.scroller.scrollToItem(firstMessageIndex)
          );
        });
      });

      return newMessageBatch;
    },

    setupIntersectionObserver() {
      this.topSentinel = document.createElement('div');
      this.topSentinel.style.height = '1px';
      this.$refs.scroller.$el.prepend(this.topSentinel);

      this.intersectionObserver = new IntersectionObserver(
        async entries => {
          if (entries[0].isIntersecting) {
            if (
              this.finished ||
              this.fetchingMessages ||
              this.scrollingToMessage
            )
              return;

            const newMessageBatch = await this.getMoreMessages();

            if (newMessageBatch.length === 0) {
              this.finished = true;
            }
          }
        },
        {
          root: this.$refs.scroller.$el,
          threshold: 1.0,
        }
      );

      this.intersectionObserver.observe(this.topSentinel);
    },

    cleanupIntersectionObserver() {
      if (this.intersectionObserver) {
        this.intersectionObserver.unobserve(this.topSentinel);
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    },

    setScrollingToMessage(value) {
      this.$store.commit('ui/SET_SCROLLING_TO_MESSAGE', value);
    },
  },
};
</script>

<style lang="scss" scoped>
.messages {
  position: relative;
  height: 100%;
  max-height: calc(100vh - 146px);

  .loading-messages-overlay {
    margin-bottom: -22px;
  }
}

.scroll-btn {
  bottom: 1rem;
  right: 1rem;
}

.scroller {
  height: 100%;
  padding: 0.25rem 0;
}

.chat-window {
  position: relative;

  &__date {
    position: absolute;
    top: 1rem;
    left: 50%;
    transform: translateX(-50%);

    padding: 0.25rem 0.75rem;
    border-radius: 0.5rem;

    background: rgba(0, 0, 0, 0.5);
    color: #fff;
  }
}

.highlight {
  background: rgba(100, 150, 210, 0.5);
}
</style>
